import { AnchorButton, TextArea } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { MimeType } from 'vet-bones/constants';
import { isEqual } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { VoiceboxMessageLine } from 'src/containers/Voicebox/Message/VoiceboxMessageLine';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import {
  isVoiceboxPendingSelector,
  voiceboxConversationIdSelector,
  voiceboxConversationSelector,
  voiceboxModeSelector,
} from 'src/selectors/voicebox';
import { VoiceboxMode } from 'src/store/voicebox/types';
import { askVoicebox, voiceboxSlice } from 'src/store/voicebox/voicebox.slice';
import * as copy from 'src/templates/copy';
import { exportToFile } from 'src/utils/exportToFile';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { createVoiceboxModelSchema } from 'src/utils/voicebox/createVoiceboxModelSchema';

export const VoiceboxDialogConversation: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isConnected = useReduxSelector(isConnectedSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);

  const { projectId } = useRouterParams();
  const diagram = useReduxSelector(diagramSnapshotSelector(projectId));

  const mode = useReduxSelector(voiceboxModeSelector);
  const modelSchema = React.useMemo(() => {
    if (!diagram || mode === VoiceboxMode.NEW) {
      return undefined;
    }
    return createVoiceboxModelSchema({
      model: diagram.model,
    });
  }, [diagram, mode]);

  const voiceboxPending = useReduxSelector(isVoiceboxPendingSelector);
  const conversationId = useReduxSelector(voiceboxConversationIdSelector);
  const conversation = useReduxSelector(voiceboxConversationSelector);

  const messages = React.useMemo(
    () =>
      conversation
        .filter((message) => !message.hidden)
        .map((message, idx) => (
          <VoiceboxMessageLine
            key={message.id}
            isLastMessage={idx === conversation.length - 1}
            message={message}
          />
        )),

    [conversation]
  );

  const isFirstMessage = !conversation.some(
    (message) => message.role === 'user'
  );
  let promptHint = '';
  if (isFirstMessage) {
    if (mode === VoiceboxMode.EXISTING) {
      promptHint = '';
    } else if (mode === VoiceboxMode.NEW) {
      promptHint = copy.components.voicebox.initialUserPrompt;
    }
  }
  const [userPrompt, setUserPrompt] = React.useState(promptHint);

  const messagesRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const disabled = Boolean(
    !isConnected || isConnecting || isRefreshingDatabases || voiceboxPending
  );

  const didConversationChange = useDidValueChange(conversation, isEqual);
  const didPendingChange = useDidValueChange(voiceboxPending);
  const didFinishPending = didPendingChange && !voiceboxPending;
  React.useEffect(() => {
    const messagesDiv = messagesRef.current;
    if (messagesDiv && (didConversationChange || didFinishPending)) {
      const messageDiv = messagesDiv.children[messagesDiv.children.length - 1];
      if (messageDiv) {
        messageDiv.scrollIntoView();
      }
    }
  }, [didConversationChange, didFinishPending]);

  React.useEffect(() => {
    const input = inputRef.current;
    if (input && !disabled) {
      input.focus();
      input.setSelectionRange(0, input.value.length || 0);
    }
  }, [disabled]);

  const exportConversation = async () => {
    const contents = JSON.stringify(conversation, null, 2);
    const filename = `voicebox-${Date.now()}.json`;
    exportToFile({
      contents,
      filename,
      mimeType: MimeType.JSON,
    });
    dispatch(voiceboxSlice.actions.exportedVoiceboxConversation({ filename }));
  };

  return (
    <>
      <div ref={messagesRef} className="sd-voicebox__messages">
        {messages}
      </div>

      <div className="sd-voicebox__actions">
        <TextArea
          autoFocus
          disabled={disabled}
          fill
          growVertically
          inputRef={inputRef}
          onChange={(e) => {
            setUserPrompt(e.target.value);
          }}
          onKeyPress={(evt) => {
            if (evt.key === 'Enter') {
              evt.preventDefault();

              if (!disabled && userPrompt.trim()) {
                setUserPrompt('');
                dispatch(
                  askVoicebox({
                    query: userPrompt,
                    existingModel: modelSchema,
                    conversationId,
                    describe: true,
                  })
                );
              }
            }
          }}
          value={userPrompt}
        />
        <Tooltip content="Download Conversation" disabled={disabled}>
          <AnchorButton
            disabled={disabled || !conversation.length}
            icon={IconNames.DOWNLOAD}
            onClick={exportConversation}
          />
        </Tooltip>
      </div>
    </>
  );
};
